import React, { useState, useEffect } from 'react';
import { supabase, getAuthClient } from '@/lib/supabase';
import { getVerifiedSession, requireVerifiedSession } from '@/lib/sessionGuard';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import DeviceDetection from '@/utils/deviceDetection';
import { handleAuthCallbackFromUrl } from '@/utils/authCallback';
import AgentChatBubble from '@/components/AgentChatBubble';
// IconLogout removed - no longer needed for auto-redirect

const VALET_SELECT_COLUMNS = 'id, auth_user_id, onboarding_completed, contract_signed, email, status, updated_at';

// Lazy getter for auth client - only initialized when actually needed for OAuth operations
// This prevents early initialization that triggers the "Multiple GoTrueClient instances" warning
const getAuthClientLazy = () => getAuthClient();

// Query functions use data client to avoid session sync issues
// The valets table has "Allow read for all users" RLS policy
async function selectValetByEmail(targetEmail) {
  if (!targetEmail) return null;

  const normalizedEmail = targetEmail.trim();
  // Throws NoVerifiedSessionError instead of querying valets with the anon key.
  await requireVerifiedSession(supabase.auth);

  const { data, error } = await supabase
    .from('valets')
    .select(VALET_SELECT_COLUMNS)
    .ilike('email', normalizedEmail)
    .order('auth_user_id', { ascending: true, nullsFirst: true })
    .order('updated_at', { ascending: false })
    .limit(5);

  if (error) {
    console.error('[Auth] Error fetching valet by email:', error);
    throw error;
  }

  if (!data || data.length === 0) {
    return null;
  }

  if (data.length > 1) {
    console.warn('[Auth] Multiple valet rows found for email, selecting best match', {
      email: normalizedEmail,
      valetIds: data.map((row) => row.id),
    });
  }

  return data.find((row) => !row.auth_user_id) ?? data[0];
}

async function selectValetByAuthUserId(authUserId) {
  if (!authUserId) return null;
  // Throws NoVerifiedSessionError instead of querying valets with the anon key.
  await requireVerifiedSession(supabase.auth, authUserId);

  // Add timeout to prevent hanging
  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(() => reject(new Error('Valet lookup timed out')), 10000)
  );

  const queryPromise = supabase
    .from('valets')
    .select(VALET_SELECT_COLUMNS)
    .eq('auth_user_id', authUserId)
    .order('updated_at', { ascending: false })
    .limit(1);

  try {
    const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

    if (error) {
      console.error('[Auth] Error fetching valet by auth_user_id:', error);
      throw error;
    }

    return data?.[0] ?? null;
  } catch (e) {
    console.error('[Auth] selectValetByAuthUserId failed:', e.message);
    throw e;
  }
}

// NOTE: Auto-creation of valet profiles removed for security
// Valet profiles must be created by HR/admin through proper channels
// This prevents random signups from creating rogue accounts

/**
 * Beautiful Valet Authentication Page
 * Handles 3 scenarios:
 * 1. First-time with Bubble account → Link auth to valet profile
 * 2. First-time new valet → Create profile + link
 * 3. Returning valet → Simple login
 */
export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user: authUser,
    loading: authLoading,
    authReady,
    rolesReady,
    logout: logoutUser,
    userRole,
    hasDashboardAccess
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isSignUp, setIsSignUp] = useState(false);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [processingCallback, setProcessingCallback] = useState(false);

  // Safety valve: clear processingCallback after 10s to prevent infinite spinner
  // If OAuth exchange fails or auth state never resolves, user can still interact
  useEffect(() => {
    if (!processingCallback) return;
    const timeout = setTimeout(() => {
      console.warn('[Auth] processingCallback safety valve — clearing after 10s timeout');
      setProcessingCallback(false);
      window.__odvAuthCallbackInProgress = false;
    }, 10000);
    return () => clearTimeout(timeout);
  }, [processingCallback]);
  const [redirectingToOAuth, setRedirectingToOAuth] = useState(false);
  const [showAuthChat, setShowAuthChat] = useState(false);
  const [authChatContext, setAuthChatContext] = useState({});

  const animatedTexts = ['GREAT', 'SERVICE', 'EVERYWHERE', 'ALL THE TIME'];

  // No scroll override needed — auth page uses fixed positioning with its own
  // overflow-y-auto container, independent of global overflow:hidden on html/body/#root.

  // Surface querystring hints (e.g., confirming emails or magic links)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isConfirming = params.get('confirming') === 'true';
    const method = params.get('method');

    if (isConfirming) {
      const successCopy =
        method === 'otp'
          ? 'Magic link confirmed! Hang tight while we sign you in.'
          : 'Email confirmed! You can finish signing in below.';

      setMessage((prev) => {
        if (prev.type === 'success' && prev.text === successCopy) {
          return prev;
        }
        return {
          type: 'success',
          text: successCopy
        };
      });
    }
  }, [location.search]);

  // ✅ Auto-redirect authenticated users who land on /auth
  // This handles: 1) users who already have a session, 2) users who just completed OAuth
  useEffect(() => {
    // If no user yet, nothing to redirect
    if (!authUser) return;

    // Wait for authReady - this means OAuth processing is complete
    if (!authReady) {
      console.log('[Auth] User authenticated, waiting for authReady...', authUser.email);
      return;
    }

    // Wait for roles to load, but DON'T gate on authLoading.
    // authLoading (AuthContext.loading) stays true until badge queries complete,
    // but those queries can hang if Supabase is slow. rolesReady is the real gate
    // since it's set in the finally block of badge resolution (always fires).
    if (!rolesReady) {
      console.log('[Auth] Waiting for user badges/roles to load...', authUser.email);
      return;
    }

    // ✅ AuthContext has established the session - clear processing state
    // This is critical: processingCallback blocks the UI, but AuthContext handles OAuth
    if (processingCallback) {
      console.log('[Auth] AuthContext session ready, clearing processingCallback');
      setProcessingCallback(false);
      window.__odvAuthCallbackInProgress = false;
    }

    // ✅ Skip auto-redirect if user explicitly came to /auth to manage their account
    const urlParams = new URLSearchParams(window.location.search);
    const stayOnAuth = urlParams.has('stay') || sessionStorage.getItem('odv-stay-on-auth');
    if (stayOnAuth) {
      console.log('[Auth] User explicitly staying on /auth page');
      sessionStorage.removeItem('odv-stay-on-auth');
      return;
    }

    // OAuth code is now cleared in AuthContext.onAuthStateChange after successful exchange
    // Do NOT clear it here as it could cause race conditions

    // Wait for userRole to be determined (AuthContext fetches badges)
    // userRole will be null initially, then set based on badge hierarchy
    // Route based on badges/role
    // Users with dashboard access badges go to web dashboard
    // Valets (default) go to mobile-optimized valet app

    // CRITICAL: Check if user has a valet profile before redirecting to /valet
    // This prevents redirect loop: /auth → /valet → (no profile) → /auth → repeat
    const checkValetAndRedirect = async () => {
      try {
        // No verified session for this user yet: don't query (it would go out as anon).
        // This effect re-runs when AuthContext updates authUser/authReady/rolesReady.
        if (!(await getVerifiedSession(supabase.auth, authUser.id))) {
          console.log('[Auth] Waiting for a verified session before the valet check');
          return;
        }
        // Quick check for valet profile
        const { data: valet } = await supabase
          .from('valets')
          .select('id, onboarding_completed, contract_signed')
          .or(`auth_user_id.eq.${authUser.id},email.ilike.${authUser.email}`)
          .limit(1)
          .maybeSingle();

        if (!valet && !hasDashboardAccess) {
          // No valet profile and no dashboard access - show error with join link
          console.log('[Auth] No valet profile found for user, showing join CTA');
          setMessage({
            type: 'error',
            text: `No valet account found for ${authUser.email}. Want to join our team? Apply at opendoorvalet.com/join`,
            showJoinLink: true
          });
          setProcessingCallback(false);
          return;
        }

        const targetRoute = hasDashboardAccess ? '/operations/assignments' : '/valet';
        console.log('[Auth] authReady, redirecting based on role...', { email: authUser.email, userRole, hasDashboardAccess, targetRoute, hasValet: !!valet });
        navigate(targetRoute, { replace: true });
      } catch (err) {
        console.error('[Auth] Error checking valet profile:', err);
        // On error, try redirect anyway
        const targetRoute = hasDashboardAccess ? '/operations/assignments' : '/valet';
        navigate(targetRoute, { replace: true });
      }
    };

    checkValetAndRedirect();
  }, [authUser, authLoading, authReady, rolesReady, userRole, hasDashboardAccess, navigate, processingCallback]);

  // Handle magic link callback and valet profile linking
    useEffect(() => {
      let mounted = true;

      const routeValetWithDeviceContext = (valetRecord, sessionUser, isNewValet = false) => {
        if (!valetRecord || !sessionUser || !mounted) return;

        DeviceDetection.logDeviceInfo();

        if (window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }

        // Check if we're already on onboarding page - don't redirect away
        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/onboard')) {
          console.log('[Auth] Already on onboarding page, staying put');
          setProcessingCallback(false);
          return;
        }

        const route = DeviceDetection.getAppropriateRoute({ valet: valetRecord });
        console.log(`[Auth] ✅ Device-aware routing → ${route}`);

        if (route === '/onboard') {
          // ✅ Distinguish between new valets (mode=new) and returning valets (mode=re-onboarding)
          const mode = isNewValet ? 'new' : 're-onboarding';
          navigate(`/onboard?email=${encodeURIComponent(sessionUser.email)}&mode=${mode}`, { replace: true });
        } else {
          navigate(route, { replace: true });
        }
      };

      // ============================================================
      // SEQUENTIAL AUTH CALLBACK HANDLING
      // ============================================================
      // Use centralized handleAuthCallbackFromUrl which:
      // 1. Detects OAuth params in URL
      // 2. Waits for Supabase to process the code (if detectSessionInUrl is enabled)
      // 3. Retries getSession() up to 3 times with delays
      // 4. Only falls back to manual code exchange if needed
      // 5. Clears auth params from URL when done
      //
      // This prevents race conditions between multiple handlers trying to
      // exchange the same single-use OAuth code.
      // ============================================================

      async function handleAuthCallback() {
        if (!mounted) return;

        // Don't handle callback if we're on onboarding page
        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/onboard')) {
          console.log('[Auth] On onboarding page, skipping auth callback handling');
          return;
        }

        // Check if there are OAuth params to handle
        const urlParams = new URLSearchParams(window.location.search);
        const hasAuthParams = urlParams.has('code') || urlParams.has('access_token') ||
                             window.location.hash.includes('access_token');

        if (!hasAuthParams) {
          return; // No OAuth callback to process
        }

        // For OAuth PKCE flow (code= in URL), AuthContext handles the exchange
        // We just need to show a loading state - the auto-redirect useEffect will handle navigation
        if (urlParams.has('code')) {
          console.log('[Auth] OAuth code detected - AuthContext handles exchange, showing loading state');
          setProcessingCallback(true);
          // Don't set window.__odvAuthCallbackInProgress - let AuthContext own the exchange
          // The auto-redirect useEffect will clear processingCallback once authReady=true
          return;
        }

        // Prevent duplicate processing for non-code flows
        if (window.__odvAuthCallbackInProgress) {
          console.log('[Auth] OAuth callback already in progress, waiting...');
          setProcessingCallback(true);
          return;
        }

        window.__odvAuthCallbackInProgress = true;
        console.log('[Auth] OAuth callback detected (implicit flow), processing...');
        setProcessingCallback(true);

        try {
          // Use centralized handler for implicit flow (access_token in URL)
          const result = await handleAuthCallbackFromUrl();

          if (!result?.session) {
            // AuthContext likely already handled this callback (GLOBAL_FLAG was set)
            // OR the session is still being established
            // Wait for AuthContext to update authUser via onAuthStateChange
            console.log('[Auth] Waiting for AuthContext to establish session...');
            // The auto-redirect useEffect will handle navigation once authUser is set
            // No timeout needed - the user will be redirected as soon as authUser is available
            return;
          }

          const session = result.session;
          console.log('[Auth] Session established:', session.user.email);

          // Now do valet profile lookup and linking
          let valetProfile = await selectValetByAuthUserId(session.user.id);
          console.log('[Auth] selectValetByAuthUserId result:', valetProfile?.id || 'null');

          if (!valetProfile) {
            console.log('[Auth] No valet linked by auth_user_id, trying email lookup');
            valetProfile = await selectValetByEmail(session.user.email);
            console.log('[Auth] selectValetByEmail result:', valetProfile?.id || 'null');
          }

          if (valetProfile) {
            // Link auth_user_id if not already linked
            if (!valetProfile.auth_user_id || valetProfile.auth_user_id !== session.user.id) {
              console.log('[Auth] Linking auth_user_id to valet profile', { valetId: valetProfile.id });

              // Use Edge Function for reliable linking. It requires the signed-in user's JWT
              // (P0 lock 2026-09-30) and only ever links session.user.id; send it explicitly.
              const { data: linkData, error: linkError } = await supabase.functions.invoke(
                'link-oauth-to-valet',
                {
                  headers: { Authorization: `Bearer ${session.access_token}` },
                  body: {
                    authUserId: session.user.id,
                    email: session.user.email,
                    valetId: valetProfile.id
                  }
                }
              );

              if (linkError || !linkData?.success) {
                console.warn('⚠️ Edge Function linking failed, trying direct update:', linkError || linkData?.error);
                const { error: updateError } = await supabase
                  .from('valets')
                  .update({
                    auth_user_id: session.user.id,
                    updated_at: new Date().toISOString()
                  })
                  .eq('id', valetProfile.id);

                if (updateError) throw updateError;
              }

              valetProfile = { ...valetProfile, auth_user_id: session.user.id };
            }

            routeValetWithDeviceContext(valetProfile, session.user);
          } else if (mounted) {
            // NO AUTO-CREATE: Valet profiles must be created by HR/admin
            // Show join team CTA instead of dead-end error
            console.log('[Auth] No valet profile found for:', session.user.email);
            window.__odvAuthCallbackInProgress = false;
            setMessage({
              type: 'error',
              text: `No valet account found for ${session.user.email}. Want to join our team? Apply at opendoorvalet.com/join`,
              showJoinLink: true
            });
            setProcessingCallback(false);
          }

        } catch (error) {
          console.error('[Auth] Auth callback error:', error);
          window.__odvAuthCallbackInProgress = false;
          if (mounted) {
            setMessage({
              type: 'error',
              text: `Authentication error: ${error.message || 'Please try again or contact support.'}`
            });
            setProcessingCallback(false);
          }
        }
      }

      // Process OAuth callback sequentially
      handleAuthCallback();

      return () => {
        mounted = false;
        // Clean up the flag on unmount
        window.__odvAuthCallbackInProgress = false;
      };
    }, [navigate]);

  // Animated text effect
  useEffect(() => {
    const timings = [1000, 1000, 2000, 3000];
    let timeoutId;

    const scheduleNext = (index) => {
      timeoutId = setTimeout(() => {
        setCurrentTextIndex((index + 1) % animatedTexts.length);
        scheduleNext((index + 1) % animatedTexts.length);
      }, timings[index]);
    };

    scheduleNext(currentTextIndex);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      if (isSignUp) {
        const { data, error } = await getAuthClientLazy().auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth?mode=signup&confirming=true`,
          },
        });

        if (error) throw error;

        setMessage({
          type: 'success',
          text: 'Check your email for the confirmation link!',
        });
      } else {
        const { data, error } = await getAuthClientLazy().auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        console.log('[Auth] ✅ Email/password login successful:', data.user.email);

        // Explicitly trigger profile lookup and routing
        setProcessingCallback(true);

        try {
          let valetProfile = await selectValetByAuthUserId(data.user.id);

          if (!valetProfile) {
            console.log('[Auth] No valet linked by auth_user_id, trying email lookup');
            valetProfile = await selectValetByEmail(data.user.email);
          }

          if (valetProfile) {
            // Link auth_user_id if not already linked
            if (!valetProfile.auth_user_id || valetProfile.auth_user_id !== data.user.id) {
              console.log('[Auth] Linking auth_user_id to valet profile');
              const { error: updateError } = await supabase
                .from('valets')
                .update({
                  auth_user_id: data.user.id,
                  updated_at: new Date().toISOString()
                })
                .eq('id', valetProfile.id);

              if (updateError) {
                console.error('[Auth] Failed to link auth_user_id:', updateError);
              } else {
                valetProfile = { ...valetProfile, auth_user_id: data.user.id };
              }
            }

            // Route to appropriate page
            const route = DeviceDetection.getAppropriateRoute({ valet: valetProfile });
            console.log('[Auth] Routing to:', route);
            navigate(route, { replace: true });
          } else {
            // NO AUTO-CREATE: Show join team CTA instead of dead-end error
            console.log('[Auth] No valet profile found for:', data.user.email);
            setMessage({
              type: 'error',
              text: `No valet account found for ${data.user.email}. Want to join our team? Apply at opendoorvalet.com/join`,
              showJoinLink: true
            });
            setProcessingCallback(false);
          }
        } catch (linkError) {
          console.error('[Auth] Profile linking error:', linkError);
          setMessage({
            type: 'error',
            text: `Profile linking failed: ${linkError.message || 'Please refresh the page or contact support.'}`
          });
          setProcessingCallback(false);
        }
      }
    } catch (error) {
      // Check if it's an invalid credentials error
      const isInvalidCredentials = error.message?.toLowerCase().includes('invalid') && 
                                   error.message?.toLowerCase().includes('credentials');
      
      setMessage({
        type: 'error',
        text: error.message || 'An error occurred during authentication',
        showForgotPassword: isInvalidCredentials,
        showAIHelp: true // Show AI chat button for auth errors
      });
      
      // Set up AI chat context for this error
      setAuthChatContext({
        email: email,
        error: error.message,
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    setRedirectingToOAuth(true);
    setMessage({ type: '', text: '' });

    // Initialize auth client only when user actually clicks OAuth button
    // This prevents early initialization that triggers the "Multiple GoTrueClient instances" warning
    const authClient = getAuthClientLazy();
    
    // Set up auth state listener if not already set up
    // This ensures we can handle the OAuth callback
    if (!window.__odv_auth_listener_setup) {
      authClient.auth.onAuthStateChange(async (_event, session) => {
        // Handle auth state changes (OAuth callbacks, etc.)
        if (session) {
          console.log('[Auth] OAuth callback received, session:', session.user.email);
        }
      });
      window.__odv_auth_listener_setup = true;
    }

    try {
      // Check if user came from onboarding page
      const fromOnboarding = window.location.search.includes('from=onboard') || 
                            document.referrer.includes('/onboard');
      const redirectTo = fromOnboarding 
        ? `${window.location.origin}/onboard`
        : `${window.location.origin}/auth`;

      const { data, error } = await authClient.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectTo,
          queryParams: {
            prompt: 'select_account',  // Force account selection every time
          },
        },
      });

      if (error) throw error;
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'An error occurred during Google authentication',
      });
      setLoading(false);
      setRedirectingToOAuth(false);
    }
  };

  const handleDiscordAuth = async () => {
    setLoading(true);
    setRedirectingToOAuth(true);
    setMessage({ type: '', text: '' });

    // Initialize auth client only when user actually clicks OAuth button
    // This prevents early initialization that triggers the "Multiple GoTrueClient instances" warning
    const authClient = getAuthClientLazy();

    // Set up auth state listener if not already set up
    // This ensures we can handle the OAuth callback
    if (!window.__odv_auth_listener_setup) {
      authClient.auth.onAuthStateChange(async (_event, session) => {
        // Handle auth state changes (OAuth callbacks, etc.)
        if (session) {
          console.log('[Auth] OAuth callback received, session:', session.user.email);
        }
      });
      window.__odv_auth_listener_setup = true;
    }

    try {
      // Check if user came from onboarding page
      const fromOnboarding = window.location.search.includes('from=onboard') ||
                            document.referrer.includes('/onboard');
      const redirectTo = fromOnboarding
        ? `${window.location.origin}/onboard`
        : `${window.location.origin}/auth`;

      const { data, error } = await authClient.auth.signInWithOAuth({
        provider: 'discord',
        options: {
          redirectTo: redirectTo,
          scopes: 'identify email',
        },
      });

      if (error) throw error;
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'An error occurred during Discord authentication',
      });
      setLoading(false);
      setRedirectingToOAuth(false);
    }
  };

  const handleAppleAuth = async () => {
    setLoading(true);
    setRedirectingToOAuth(true);
    setMessage({ type: '', text: '' });

    // Initialize auth client only when user actually clicks OAuth button
    const authClient = getAuthClientLazy();

    // Set up auth state listener if not already set up
    if (!window.__odv_auth_listener_setup) {
      authClient.auth.onAuthStateChange(async (_event, session) => {
        if (session) {
          console.log('[Auth] OAuth callback received, session:', session.user.email);
        }
      });
      window.__odv_auth_listener_setup = true;
    }

    try {
      // Check if user came from onboarding page
      const fromOnboarding = window.location.search.includes('from=onboard') ||
                            document.referrer.includes('/onboard');
      const redirectTo = fromOnboarding
        ? `${window.location.origin}/onboard`
        : `${window.location.origin}/auth`;

      const { data, error } = await authClient.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: redirectTo,
        },
      });

      if (error) throw error;
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'An error occurred during Apple authentication',
      });
      setLoading(false);
      setRedirectingToOAuth(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const { data, error } = await getAuthClientLazy().auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth?method=otp&confirming=true`,
        },
      });

      if (error) throw error;

      setMessage({
        type: 'success',
        text: 'Check your email! Click the link to sign in.',
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'An error occurred sending the magic link',
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle logout for switching accounts
  const handleLogoutAndRetry = async () => {
    console.log('[Auth] User requesting to switch accounts...');
    setLoading(true);

    try {
      await logoutUser();
      console.log('[Auth] Logout complete, clearing form...');
      setEmail('');
      setPassword('');
      setMessage({
        type: 'success',
        text: 'Session cleared. You can now log in with a different account.'
      });
      setLoading(false); // Important: clear loading state
      setProcessingCallback(false);
    } catch (error) {
      console.error('[Auth] Logout error:', error);
      setMessage({
        type: 'error',
        text: 'Could not log out. Please try refreshing the page.'
      });
      setLoading(false);
    }
  };

  // BYPASS: Skip auth loading check - show login form immediately
  // The old check was causing infinite spinners
  if (false && authLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat relative overflow-y-auto"
        style={{ backgroundImage: 'url(/parkology-sunset.jpg)' }}
      >
        {/* Overlay for better text visibility */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

        {/* Content */}
        <div className="text-center relative z-10">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#AEA36D] mb-4"></div>
          <p className="text-white text-lg font-medium drop-shadow-lg">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show loading overlay when redirecting to OAuth provider
  // This prevents the white flash before Discord/Google page loads
  if (redirectingToOAuth) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat relative overflow-y-auto"
        style={{ backgroundImage: 'url(/parkology-sunset.jpg)' }}
      >
        {/* Overlay for better text visibility */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

        {/* Content */}
        <div className="text-center relative z-10">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#AEA36D] mb-4"></div>
          <p className="text-white text-lg font-medium drop-shadow-lg">Redirecting to authentication...</p>
        </div>
      </div>
    );
  }

  // Show loading spinner only during processing callback
  // Auth redirect happens automatically in useEffect, no need to block UI
  if (processingCallback) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat relative overflow-y-auto"
        style={{ backgroundImage: 'url(/parkology-sunset.jpg)' }}
      >
        {/* Overlay for better text visibility */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

        {/* Content */}
        <div className="text-center relative z-10">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#AEA36D] mb-4"></div>
          <p className="text-white text-lg font-medium drop-shadow-lg">Signing you in...</p>
        </div>
      </div>
    );
  }

  // Show account selection if already logged in
  // BUT only if:
  // 1. User is authenticated
  // 2. Not processing a callback (no fresh auth happening)
  // 3. Message indicates logout was successful (user explicitly chose to switch)
  const showAccountConfirmation = authUser && !processingCallback && message.type === 'success' && message.text.includes('Session cleared');

  if (showAccountConfirmation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-2xl overflow-hidden border border-white/10">
            {/* Header */}
            <div className="bg-gradient-to-br from-black via-gray-900 to-black p-8 text-center border-b border-white/10">
              <h2 className="text-sm font-semibold text-gray-400 mb-2 tracking-wider">
                OPEN DOOR VALET
              </h2>
              <h1 className="text-3xl font-bold text-[#AEA36D] tracking-widest">
                READY TO LOGIN
              </h1>
            </div>

            {/* Content */}
            <div className="p-8 text-center">
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-500/20 mb-4">
                  <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-gray-300 mb-4">{message.text}</p>
                <p className="text-gray-400 text-sm mb-6">
                  You can now log in with your account.
                </p>
              </div>

              {/* Clear message and show form */}
              <Button
                onClick={() => setMessage({ type: '', text: '' })}
                className="w-full bg-[#AEA36D] hover:bg-[#9A9360] text-white font-medium py-3 rounded-lg transition-all"
              >
                Continue to Login
              </Button>

              <p className="text-gray-500 text-xs mt-6">
                You'll see the login form below.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-6 text-gray-400 text-sm">
            <p className="italic">Hospitality, Driven.</p>
            <p className="mt-2">© 2025 Open Door Valet. All rights reserved.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 overflow-y-auto overflow-x-hidden bg-gradient-to-br from-black via-gray-900 to-black z-50">
      <div className="min-h-full flex items-start md:items-center justify-center py-8 px-4 relative">
      {/* Background Logo */}
      <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none">
        <img
          src="/odv-logo-car.png"
          alt="ODV Logo"
          className="w-[800px] h-auto"
        />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Main Card */}
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-2xl overflow-hidden border border-white/10">
          {/* Header Section with Animation */}
          <div className="bg-gradient-to-br from-black via-gray-900 to-black p-8 text-center border-b border-white/10">
            <h2 className="text-sm font-semibold text-gray-400 mb-2 tracking-wider">
              OPEN DOOR VALET
            </h2>
            <div className="h-16 flex items-center justify-center relative">
              <div className="relative">
                <h1 className="text-4xl font-bold text-[#AEA36D] transition-all duration-500 ease-in-out transform animate-pulse tracking-widest">
                  {animatedTexts[currentTextIndex]}
                </h1>
                <div
                  className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 bg-[#AEA36D] opacity-30 blur-sm rounded-full transition-all duration-500"
                  style={{
                    width: `${animatedTexts[currentTextIndex].length * 0.5}ch`,
                    height: '4px'
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div className="p-8">
            {/* Message Alert */}
            {message.text && (
              <Alert
                className={`mb-6 ${
                  message.type === 'success'
                    ? 'bg-green-500/20 border-green-500/50 text-green-100'
                    : 'bg-red-500/20 border-red-500/50 text-red-100'
                }`}
              >
                <AlertDescription>
                  <div>
                    {message.text}
                    {message.showForgotPassword && (
                      <div className="mt-2">
                        <button
                          onClick={() => navigate('/auth/reset-password')}
                          className="text-yellow-300 underline hover:text-yellow-200 font-medium"
                        >
                          Click here to reset your password
                        </button>
                      </div>
                    )}
                    {message.showJoinLink && (
                      <div className="mt-3 flex flex-col gap-2">
                        <a
                          href="https://opendoorvalet.com/join"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-[#AEA36D] hover:bg-[#9A9360] text-black px-4 py-2 rounded-lg font-medium transition-colors inline-flex items-center justify-center gap-2"
                        >
                          Join Our Team
                        </a>
                        {authUser && (
                          <button
                            onClick={handleLogoutAndRetry}
                            className="text-gray-300 hover:text-white text-xs underline transition-colors"
                          >
                            Try a different account
                          </button>
                        )}
                      </div>
                    )}
                    {message.showAIHelp && (
                      <div className="mt-3">
                        <button
                          onClick={() => setShowAuthChat(true)}
                          className="bg-[#AEA36D] hover:bg-[#9A9360] text-black px-4 py-2 rounded-lg font-medium transition-colors inline-flex items-center gap-2"
                        >
                          Get AI Help
                          <span className="text-xs opacity-75">Chat with assistant</span>
                        </button>
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {!message.text && (
              <Alert className="mb-6 bg-gray-600/30 border-[#AEA36D]/40">
                <AlertDescription className="text-[#AEA36D]">
                  Existing valet migrating from Bubble? Skip the login loop and head straight to the{" "}
                  <Link to="/onboard" className="underline text-blue-400 font-semibold hover:text-blue-300">
                    self-service re-onboarding portal
                  </Link>{" "}
                  to finish your setup.
                </AlertDescription>
              </Alert>
            )}

            {/* Auto-redirect if already logged in - no UI needed */}

            {/* Email/Password Form */}
            <>
                <form onSubmit={handleEmailAuth} className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-[#AEA36D] mb-2">
                      Email
                    </label>
                    <div className="rounded-lg bg-gradient-to-r from-[#AEA36D] via-white to-[#AEA36D] p-[2px]">
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@opendoorvalet.com"
                        required
                        className="w-full bg-black/60 border-0 text-white placeholder:text-gray-400 focus:ring-2 focus:ring-[#AEA36D]/50 rounded-lg"
                      />
                    </div>
                  </div>

                  {!message.type && (
                    <div>
                      <label className="block text-sm font-medium text-[#AEA36D] mb-2">
                        Password <span className="text-xs text-gray-500">(or use magic link below)</span>
                      </label>
                      <div className="rounded-lg bg-gradient-to-r from-[#AEA36D] via-white to-[#AEA36D] p-[2px]">
                        <Input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••"
                          className="w-full bg-black/60 border-0 text-white placeholder:text-gray-400 focus:ring-2 focus:ring-[#AEA36D]/50 rounded-lg"
                        />
                      </div>
                      <div className="mt-2 text-right">
                        <button
                          type="button"
                          onClick={() => navigate('/auth/reset-password')}
                          className="text-xs text-[#AEA36D] hover:text-[#9A9360] transition-colors"
                        >
                          Forgot password?
                        </button>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading || !password}
                    className="w-full bg-[#AEA36D] hover:bg-[#9A9360] text-white font-medium py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    title={!password ? "Enter password or use magic link below" : ""}
                  >
                    {loading
                      ? 'Loading...'
                      : isSignUp
                      ? 'Create Account'
                      : 'Sign In'}
                  </Button>
                </form>

                {/* Divider */}
                <div className="relative mb-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/20"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-transparent text-gray-400">
                      Or continue with
                    </span>
                  </div>
                </div>

                {/* Google Auth */}
                <Button
                  onClick={handleGoogleAuth}
                  disabled={loading}
                  variant="outline"
                  className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20 font-medium py-3 rounded-lg transition-all mb-3"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#EA4335"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continue with Google
                </Button>

                {/* Discord Auth */}
                <Button
                  onClick={handleDiscordAuth}
                  disabled={loading}
                  variant="outline"
                  className="w-full bg-[#5865F2]/20 border-[#5865F2]/40 text-white hover:bg-[#5865F2]/30 font-medium py-3 rounded-lg transition-all mb-3"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                  </svg>
                  Continue with Discord
                </Button>

                {/* Apple Auth */}
                <Button
                  onClick={handleAppleAuth}
                  disabled={loading}
                  variant="outline"
                  className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20 font-medium py-3 rounded-lg transition-all mb-4"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  Continue with Apple
                </Button>

                {/* Magic Link */}
                <Button
                  onClick={handleMagicLink}
                  disabled={loading || !email}
                  variant="outline"
                  className="w-full bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed font-medium py-2 rounded-lg transition-all text-sm"
                  title={!email ? "Enter your email first" : "Click to receive a sign-in link via email"}
                >
                  {!email ? '📧 Enter email above to send magic link' : 'Send Magic Link Instead'}
                </Button>

                {/* Toggle Sign Up / Sign In */}
                <div className="mt-6 text-center">
                  <button
                    onClick={() => {
                      setIsSignUp(!isSignUp);
                      setMessage({ type: '', text: '' });
                    }}
                    className="text-[#AEA36D] hover:text-[#9A9360] text-sm font-medium transition-colors"
                  >
                    {isSignUp
                      ? 'Already have an account? Sign in'
                      : "Don't have an account? Sign up"}
                  </button>
                </div>
              </>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-gray-400 text-sm">
          <p className="italic">Hospitality, Driven.</p>
          <p className="mt-2">© 2025 Open Door Valet. All rights reserved.</p>
          
          <div className="mt-4">
            <button
              onClick={() => {
                setAuthChatContext({ email: 'test@example.com', error: 'Demo mode' });
                setShowAuthChat(true);
              }}
              className="bg-[#AEA36D] hover:bg-[#9A9360] text-black px-4 py-2 rounded-lg font-medium transition-colors text-sm"
            >
              Demo AI Assistant
            </button>
          </div>
        </div>
      </div>

      {/* AI Authentication Assistant */}
      {showAuthChat && (
        <AgentChatBubble
          authMode={true}
          authContext={{
            email: email,
            error: message.text,
            area: 'authentication'
          }}
          onClose={() => setShowAuthChat(false)}
        />
      )}
      </div>
    </div>
  );
}
